import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Loader from "../../components/Global/Loader";
import { getDisplayValue } from "../../utils/displayValue";

const ViewApplication = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state;
  const [applicationData, setApplicationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  // console.log('Application data: ', applicationData)

  const currentStep =
    applicationData?.status === 'Pending'
      ? 1
      : applicationData?.status === 'In Process'
        ? 2
        : ((applicationData?.status === 'Accepted') || applicationData?.status === 'Rejected')
          ? 3
          : 0;

  useEffect(() => {
    // Fetch application details
    const fetchApplication = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/application/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setApplicationData(response.data.data);
        setRemarks(response.data.data?.remarks || "");
      } catch (error) {
        // console.error("Error fetching application:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplication();
  }, [id]);


  // Function to Download Documents
  const handleDownloadDocuments = async () => {
    try {
      setDownloading(true);
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/application/download/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob", // To handle binary file response
        }
      );

      // Create a blob URL and trigger the download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${applicationData?.customApplicationId}-documents.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      // console.error("Error downloading documents:", error);
      alert("Failed to download documents. Please try again later.");
    } finally {
      setDownloading(false);
      setLoading(false);
    }
  };


  const updateApplication = async () => {
    try {
      setLoading(true);
      if (!remarks.trim()) {
        alert("Please enter remarks before closing the application.");
        return;
      }

      const updatedData = {
        remarks: remarks,
      };

      await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/application/update/${id}`,
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Update local state to reflect changes
      setApplicationData((prev) => ({
        ...prev,
        remarks: remarks,
      }));

      setIsSubmitted(true);
    } catch (error) {
      // console.error("Error updating application:", error);
      alert(error?.response?.data?.message || "Failed to submit remarks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!applicationData) return <p className="text-red-500 text-center min-h-screen">Application not found</p>;

  return (
    <div id="main-content" className="relative mt-16 w-full pl-36 p-8 bg-[#f4f8fb]">
      {/* Loader */}
      {loading && <Loader />}

      {
        isSubmitted && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
              {/* Modal Content */}
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Submission Successful
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Your remarks have been successfully submitted. Thank you for your valuable feedback.
              </p>
              <div className="flex justify-end">
                {/* Proceed Button */}
                <button
                  onClick={() => {
                    setIsSubmitted(false)
                    navigate('/consultant/applications')
                  }}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Content Wrapper */}
      <div className="flex flex-wrap space-x-6">
        {/* Right Card */}
        <div className="px-10 py-6 bg-white rounded shadow-lg w-full">
          {/* Personal Info Heading */}
          <h2 className="text-gray-800 text-lg font-bold mb-4">
            Student Application
          </h2>

          <div className="flex items-center justify-between mb-4 gap-28">

            <div className="mb-6">
              <img src={applicationData?.profileURL ? applicationData.profileURL : "/Assets/default_user.png"} alt="User Profile" className="size-[122px] object-cover border border-gray-300" />
            </div>
            {/* Milestone Progress */}
            {/* Determine the current step from status */}

            <div className="flex items-center w-[60%] justify-center bg-white p-6 rounded-md">
              {/* Step 1: Application Created */}
              <div className="flex flex-col items-center mt-3 text-xs w-[48px]">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${currentStep >= 1
                    ? "border-blue-600 bg-blue-600 text-white" // Checked (filled with checkmark)
                    : "border-gray-400 bg-white text-gray-400" // Incomplete (empty circle)
                    }`}
                >
                  {currentStep >= 1 ? <i className="fas fa-check"></i> : null} {/* Checkmark for completed steps */}
                </div>
                <div className="text-center text-nowrap">Application Created</div>
              </div>

              {/* Progress bar between step 1 and step 2 */}
              <div className="grow h-[2px] flex">
                <div
                  className={`grow ${currentStep >= 1 ? "bg-blue-600" : "bg-gray-400"}`}
                ></div>
              </div>

              {/* Step 2: Application In Process */}
              <div className="flex flex-col items-center mt-3 text-xs w-[48px]">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${currentStep >= 2
                    ? "border-blue-600 bg-blue-600 text-white" // Checked (filled with checkmark)
                    : currentStep === 1
                      ? "border-blue-600 bg-white text-blue-600" // Current step (circle)
                      : "border-gray-400 bg-white text-gray-400" // Incomplete (empty circle)
                    }`}
                >
                  {currentStep >= 2 ? (
                    <i className="fas fa-check"></i> // Checkmark for completed steps
                  ) : currentStep === 1 ? (
                    <i className="fas fa-circle"></i> // Circle for current step
                  ) : null}
                </div>
                <div className="text-center text-nowrap">Application In Process</div>
              </div>

              {/* Progress bar between step 2 and step 3 */}
              <div className="grow h-[2px] flex">
                <div
                  className={`grow ${currentStep >= 2 ? "bg-blue-600" : "bg-gray-400"}`}
                ></div>
              </div>

              {/* Step 3: Closed */}
              <div className="flex flex-col items-center mt-3 text-xs w-[48px]">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${currentStep >= 3
                    ? "border-blue-600 bg-blue-600 text-white" // Checked (filled with checkmark)
                    : currentStep === 2
                      ? "border-blue-600 bg-white text-blue-600" // Current step (circle)
                      : "border-gray-400 bg-white text-gray-400" // Incomplete (empty circle)
                    }`}
                >
                  {currentStep >= 3 ? (
                    <i className="fas fa-check"></i> // Checkmark for completed steps
                  ) : currentStep === 2 ? (
                    <i className="fas fa-circle"></i> // Circle for current step
                  ) : null}
                </div>
                <div className="text-center text-nowrap">Closed</div>
              </div>
            </div>

          </div>

          <hr className="border-t border-gray-300 mb-4" />
          <h2 className="text-gray-800 text-lg font-bold mb-4">
            Personal Info
          </h2>


          {/* First Row of Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6 px-4 mt-7">
            <InfoField label="Title" value={applicationData?.title || ""} />
            <InfoField label="Full Name" value={applicationData?.fullName || ""} />
            <InfoField label="Father's Name" value={applicationData?.fatherName || ""} />
            <InfoField label="Gender" value={applicationData?.gender || ""} />
          </div>

          {/* Second Row of Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6 px-4">
            <InfoField label="Date of Birth" value={applicationData?.dateOfBirth?.split("T")[0] || ""} />
            <InfoField label="Marital Status" value={applicationData?.maritalStatus || ""} />
            <InfoField label="Cell Phone" value={applicationData?.cellPhone || ""} />
            <InfoField label="City of Residence" value={applicationData?.cityOfResidence || ""} />
          </div>

          {/* Third Row of Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6 px-4">
            <InfoField label="Email" value={applicationData?.email || ""} />
            <InfoField label="CNIC" value={applicationData?.cnic || ""} />
            <InfoField label="Nationality" value={applicationData?.nationality || ""} />
            <InfoField label="Passport Number" value={applicationData?.passportNumber || ""} />
          </div>

          <hr className="border-t border-gray-300 mb-4" />

          {/* Area Of Interest */}
          <h2 className="text-gray-800 text-lg font-bold mt-6 mb-4">Area Of Interest</h2>
          <div className="grid lg:grid-cols-4 sm:grid-cols-2 gap-4 mb-6 px-4">
            <InfoField label="Country" value={applicationData?.country || ""} />
            <InfoField label="Programme" value={applicationData?.programme || ""} />
            <InfoField label="City" value={applicationData?.city || ""} />
            <InfoField label="University" value={applicationData?.university || ""} />
            {/* <InfoField label="Majors" value={applicationData?.major[0] || ""} />
            {applicationData?.major.length>0 && applicationData?.major.map((major, index) => (
            <InfoField key={index} value={major} />
          ))} */}
          </div>

          <section className="mt-4 mb-8 px-4">
            <h2 className="text-gray-800 text-lg font-semibold mb-2">Majors</h2>
            {applicationData?.major && applicationData.major.length > 0 ? (
              <ol className="list-decimal ml-6 grid grid-cols-4 space-y-2">
                {applicationData.major.map((major, index) => (
                  <li key={index} className="text-gray-900 mt-1 text-lg">
                    {getDisplayValue(major)}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-gray-500">No majors selected</p>
            )}
          </section>



          {/* <hr className="border-t border-gray-300 mb-4" /> */}

          {/* Documents */}
          <h2 className="text-gray-800 text-lg font-bold mt-6 mb-4">Download Documents</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="relative">
              <label class="text-sm font-bold text-gray-700">Download files</label>
              <a
                onClick={handleDownloadDocuments}
                className="block text-center p-2 border rounded bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
                disabled={downloading}
              >
                {downloading ? "Downloading..." : "Download"}
              </a>
              <p className="text-gray-500 text-xs mt-1">Click the button to download .zip file of the documents</p>
            </div>
          </div>

          <hr className="border-t border-gray-300 mb-4" />

          {/* Comments Section */}
          <div className="flex justify-end mt-10 mr-5 w-full">
            <div className="w-full mb-4">
              <label htmlFor="comments" className="block font-bold text-gray-700">
                Remarks
              </label>
              <textarea
                id="comments"
                name="comments"
                rows="4"
                className="w-full p-2 border rounded mt-4"
                placeholder="Enter your comments here..."
                value={remarks} // Controlled input
                onChange={(e) => setRemarks(e.target.value)} // Update remarks state
                readOnly={(applicationData?.remarks !== "")}
              ></textarea>
            </div>
          </div>

          {/* Close Application Button */}
          <div className="flex justify-end">
            <button
              onClick={updateApplication}
              disabled={applicationData?.remarks !== ""}
              className={`px-4 py-2 rounded border 
                ${(applicationData?.remarks !== "")
                  ? "bg-white text-blue-300 border-blue-300 cursor-not-allowed"
                  : "bg-white text-blue-500 border-blue-500 hover:bg-blue-500 hover:text-white cursor-pointer"
                }`}
            >
              {(applicationData?.remarks === "") ? "Submit Remarks" : "Remarks Added"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// InfoField Component for reusability
const InfoField = ({ label, value }) => (
  <div className="relative">
    <span className="text-gray-800 text-base font-semibold">{label}</span>
    <p className="text-gray-900 mt-1">{getDisplayValue(value, "")}</p>
  </div>
);

export default ViewApplication
